<template>
  <div class="variant-selector">
    <!-- Quantity table mode: when too many choices (>10) -->
    <template v-if="useQuantityMode">
      <div class="variant-quantities-wrapper">
        <table class="table table-striped variant-quantities-table">
          <thead>
            <tr>
              <th v-for="name of columns" :key="'column-' + name" class="name-column">
                {{ formatLabel(name) }}
              </th>
              <th>Quantity</th>
              <th class="action-column"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row of quantityRows" :key="'row-' + row.id">
              <td v-for="name of columns" :key="'cell-' + row.id + '-' + name" class="name-column">
                {{ row.options[name] }}
              </td>
              <td>
                <input
                  type="number"
                  class="form-control quantity-input"
                  v-model.number="selectedQuantities[row.id].quantity"
                  min="0"
                  :max="expectedQuantity"
                  @input="onQuantityChange"
                />
              </td>
              <td class="action-column">
                <button type="button" class="clear-btn" @click="clearQuantity(row.id)">
                  <span>&times;</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="quantity-summary">
          Total selected: {{ totalSelectedQuantity }} / {{ expectedQuantity }}
        </div>
      </div>
    </template>

    <!-- Dropdown mode: standard variant selection -->
    <template v-else>
      <div class="variant-selectors" v-for="index in dropdownSelectorCount" :key="index">
        <div class="bundle-index" v-if="dropdownSelectorCount > 1">
          <span class="bundle-index-text">Bundle Item {{index}}</span>
        </div>
        <div class="variant-group" v-for="name of variantNames" :key="'row-' + name + '-' + index">
          <label :for="'id-' + name + '-' + index">
            {{ formatLabel(name) }}
            <span v-if="dropdownSelectorCount > 1"> #{{ index }}</span>
          </label>
          <select
            :id="'id-' + name + '-' + index"
            class="variant-dropdown form-control"
            v-model="selections[name][index - 1]"
            @change="onSelectionChange"
            required
          >
            <option :value="null">-- Please Select --</option>
            <option
              v-for="value in variantOptions[name]"
              :key="'variant-' + index + '-' + name + '-' + value"
              :value="value"
            >
              {{ value }}
            </option>
          </select>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'VariantSelector',
  props: {
    // Array of variant objects: [{id, options: [{name, value}, ...]}, ...]
    variants: {
      type: Array,
      required: true
    },
    // Number of items to select variants for
    quantity: {
      type: Number,
      default: 1
    },
    // For bundled variants - multiplier for quantity
    bundleQuantity: {
      type: Number,
      default: 1
    },
    // Pre-selected variant (for editing existing selections)
    initialVariant: {
      type: Object,
      default: null
    },
    // Pre-selected bundled variants array
    initialBundledVariants: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      variantOptions: {},
      selections: {},
      idMap: {},
      selectedQuantities: {},
      quantityRows: [],
      columns: []
    }
  },
  computed: {
    expectedQuantity() {
      return this.quantity * this.bundleQuantity
    },
    // For dropdown mode, we only show multiple selectors for bundled products
    // For standard products (bundleQuantity === 1), we show one selector regardless of quantity
    dropdownSelectorCount() {
      return this.bundleQuantity > 1 ? this.expectedQuantity : 1
    },
    variantNames() {
      return Object.keys(this.variantOptions).sort()
    },
    // Determine if we should use quantity table mode
    // Only use quantity mode for bundled products with many choices
    useQuantityMode() {
      if (this.bundleQuantity > 1 && this.expectedQuantity > 1) {
        const totalChoices = this.variantNames.length * this.expectedQuantity
        return totalChoices > 10
      }
      return false
    },
    // Total quantity selected in quantity mode
    totalSelectedQuantity() {
      let total = 0
      for (const row of Object.values(this.selectedQuantities)) {
        const qty = parseInt(row.quantity)
        total += isNaN(qty) ? 0 : qty
      }
      return total
    },
    // Check if all selections are made (dropdown mode)
    isCompleteDropdown() {
      for (const name of this.variantNames) {
        for (let i = 0; i < this.dropdownSelectorCount; i++) {
          if (!this.selections[name] || this.selections[name][i] === null) {
            return false
          }
        }
      }
      return this.variantNames.length > 0
    },
    // Check if quantity selections are complete
    isCompleteQuantity() {
      return this.totalSelectedQuantity === this.expectedQuantity
    },
    // Overall completion status
    isComplete() {
      if (this.useQuantityMode) {
        return this.isCompleteQuantity
      }
      return this.isCompleteDropdown
    },
    // Get current selected variants as [{variant: object, quantity}] from dropdown mode
    selectedVariantsFromDropdown() {
      if (!this.isCompleteDropdown) {
        return []
      }

      const quantities = {}
      const names = this.variantNames

      for (let i = 0; i < this.dropdownSelectorCount; i++) {
        const pairs = []

        for (const optionName of names) {
          const value = this.selections[optionName][i]
          pairs.push(optionName + '=' + value)
        }

        const id = this.idMap[pairs.sort().join(',')]

        if (id !== undefined) {
          if (id in quantities) {
            quantities[id] += 1
          } else {
            quantities[id] = 1
          }
        }
      }

      const result = []
      for (const [id, quantity] of Object.entries(quantities)) {
        const fullVariant = this.variants.find(v => v.id === parseInt(id))
        // For non-bundled products, multiply the quantity by the parent quantity
        // For bundled products, the loop already counted correctly
        const finalQuantity = this.bundleQuantity > 1 ? quantity : quantity * this.quantity
        result.push({
          variant: fullVariant || { id: parseInt(id) },
          quantity: finalQuantity
        })
      }

      return result
    },
    // Get current selected variants as [{variant: object, quantity}] from quantity mode
    selectedVariantsFromQuantity() {
      const result = []

      for (const [id, row] of Object.entries(this.selectedQuantities)) {
        const qty = parseInt(row.quantity)
        if (!isNaN(qty) && qty > 0) {
          const fullVariant = this.variants.find(v => v.id === parseInt(id))
          result.push({
            variant: fullVariant || { id: parseInt(id) },
            quantity: qty
          })
        }
      }

      return result
    },
    // Combined selected variants based on current mode
    selectedVariants() {
      if (this.useQuantityMode) {
        return this.selectedVariantsFromQuantity
      }
      return this.selectedVariantsFromDropdown
    }
  },
  watch: {
    variants: {
      immediate: true,
      handler() {
        this.processVariants()
      }
    },
    quantity() {
      // For non-bundled products, don't reprocess - just emit the change
      // since the quantity is multiplied in selectedVariantsFromDropdown
      if (this.bundleQuantity === 1) {
        this.$nextTick(() => {
          this.emitChange()
        })
      } else {
        this.processVariants()
      }
    },
    bundleQuantity() {
      this.processVariants()
    }
  },
  methods: {
    formatLabel(name) {
      return name.toLowerCase().replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()))
    },
    processVariants() {
      if (!this.variants || !this.variants.length) {
        this.variantOptions = {}
        this.selections = {}
        this.idMap = {}
        this.selectedQuantities = {}
        this.quantityRows = []
        this.columns = []
        return
      }

      const variantOptionMap = {}
      const idMap = {}
      const selectedQuantities = {}

      // Build unique set of variant options and map them to their ids
      this.variants.forEach(variant => {
        const pairs = []
        const options = {}

        variant.options.forEach(option => {
          if (!(option.name in variantOptionMap)) {
            variantOptionMap[option.name] = {}
          }
          variantOptionMap[option.name][option.value] = true
          pairs.push(option.name + '=' + option.value)
          options[option.name] = option.value
        })

        const key = pairs.sort().join(',')
        idMap[key] = variant.id
        selectedQuantities[variant.id] = { quantity: 0, options: options, key: key }
      })

      // Convert to sorted lists
      const variantOptions = {}
      const selections = {}

      // For dropdown mode, only create slots for dropdownSelectorCount (1 for non-bundled, expectedQuantity for bundled)
      const selectorCount = this.bundleQuantity > 1 ? this.expectedQuantity : 1

      Object.keys(variantOptionMap).forEach(optionName => {
        variantOptions[optionName] = Object.keys(variantOptionMap[optionName]).sort()
        selections[optionName] = []

        for (let i = 0; i < selectorCount; i++) {
          selections[optionName].push(null)
        }
      })

      // Apply initial selections if provided
      if (this.initialBundledVariants && this.initialBundledVariants.length) {
        let startIndex = 0

        this.initialBundledVariants.forEach(bundledVariant => {
          // Update quantity mode data
          if (selectedQuantities[bundledVariant.variant.id]) {
            selectedQuantities[bundledVariant.variant.id].quantity = bundledVariant.quantity
          }

          // Update dropdown mode data (only for bundled products)
          if (this.bundleQuantity > 1) {
            for (let i = 0; i < bundledVariant.quantity && (startIndex + i) < selectorCount; i++) {
              for (const option of bundledVariant.variant.options) {
                if (selections[option.name]) {
                  selections[option.name][startIndex + i] = option.value
                }
              }
            }
            startIndex += bundledVariant.quantity
          }
        })
      } else if (this.initialVariant && this.initialVariant.options) {
        // Update quantity mode data
        if (selectedQuantities[this.initialVariant.id]) {
          selectedQuantities[this.initialVariant.id].quantity = this.expectedQuantity
        }

        // Update dropdown mode data - for non-bundled, only set the first selector
        for (let i = 0; i < selectorCount; i++) {
          for (const option of this.initialVariant.options) {
            if (selections[option.name]) {
              selections[option.name][i] = option.value
            }
          }
        }
      }

      // Build quantity rows and columns for quantity mode
      const quantityRows = []
      const optionNames = {}

      for (const [id, row] of Object.entries(selectedQuantities)) {
        row.id = id
        quantityRows.push(row)
        Object.keys(row.options).forEach(name => {
          optionNames[name] = true
        })
      }

      this.variantOptions = variantOptions
      this.selections = selections
      this.idMap = idMap
      this.selectedQuantities = selectedQuantities
      this.quantityRows = quantityRows.sort((a, b) => a.key.localeCompare(b.key))
      this.columns = Object.keys(optionNames).sort()

      // Emit initial state
      this.$nextTick(() => {
        this.emitChange()
      })
    },
    onSelectionChange() {
      this.emitChange()
    },
    onQuantityChange() {
      this.emitChange()
    },
    emitChange() {
      this.$emit('change', {
        isComplete: this.isComplete,
        selectedVariants: this.selectedVariants,
        useQuantityMode: this.useQuantityMode
      })
    },
    clearQuantity(variantId) {
      if (this.selectedQuantities[variantId]) {
        this.selectedQuantities[variantId].quantity = 0
        this.emitChange()
      }
    },
    // Public method to get final selection (can be called by parent)
    getSelection() {
      if (!this.isComplete) {
        return null
      }
      return { variants: this.selectedVariants }
    },
    // Public method to validate and return selection or show error
    validate() {
      if (this.useQuantityMode) {
        if (!this.isCompleteQuantity) {
          return {
            valid: false,
            message: 'Total quantity must be ' + this.expectedQuantity.toString()
          }
        }
      } else {
        if (!this.isCompleteDropdown) {
          return { valid: false, message: 'Please fill out all variant options' }
        }
      }
      return { valid: true, variants: this.selectedVariants }
    }
  }
}
</script>

<style scoped>
.variant-selector {
  width: 100%;
}

.variant-selectors {
  margin-bottom: 1rem;
  gap: 10px;
}

.variant-group {
  margin-bottom: 0.75rem;
}

.variant-group label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.25rem;
  font-size: 14px;
}

.variant-dropdown {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-height: 42px;
}

.variant-dropdown:focus {
  outline: none;
  border-color: var(--theme_color, #007bff);
}

/* Quantity mode styles */
.variant-quantities-wrapper {
  width: 100%;
  overflow-x: auto;
}

.variant-quantities-table {
  width: 100%;
  margin-bottom: 1rem;
  table-layout: auto;
  border-collapse: collapse;
}

.variant-quantities-table th,
.variant-quantities-table td {
  padding: 0.75rem !important;
  vertical-align: middle !important;
}

.variant-quantities-table .name-column {
  text-transform: capitalize;
  vertical-align: middle !important;
}

.variant-quantities-table .action-column {
  width: 60px;
  min-width: 60px;
  max-width: 60px;
  text-align: center;
  padding: 0.75rem !important;
}

.quantity-input {
  width: 80px;
  text-align: center;
  border: 1px solid #ddd !important;
  background-color: #fff !important;
  border-radius: 4px;
}

.quantity-input:focus {
  border-color: var(--theme_color) !important;
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
}

.clear-btn {
  padding: 0.25rem 0.5rem !important;
  font-size: 1.5rem !important;
  line-height: 1 !important;
  color: var(--theme_color) !important;
  background: transparent !important;
  border: 1px solid var(--theme_color) !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  border-radius: 4px !important;
  text-decoration: none !important;
  display: inline-block !important;
  position: relative !important;
  margin: 0 !important;
}

.clear-btn:hover {
  color: #fff !important;
  background-color: var(--theme_color) !important;
  border-color: var(--theme_color) !important;
  text-decoration: none !important;
}

.clear-btn:focus {
  outline: none !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.quantity-summary {
  font-size: 14px;
  color: #666;
  text-align: right;
  margin-top: 0.5rem;
}

.bundle-index {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
}

.bundle-index-text {
  font-size: 18px;
  font-weight: bold;
  padding-bottom: 5px;
  border-bottom: 2px solid var(--theme_color);
}

.variant-selector-container .variant-selectors {
  width: 80%;
  justify-self: center;
}

@media screen and (max-width: 767px) {
  .variant-selectors {
    width: 100%;
  }
}
</style>
